import axios from "axios";

export interface Vendor {
  vendorId: string;
  companyName: string;
}

export interface VendorHierarchy {
  subVendors: Record<string, string[]>;
}

export interface VendorResponse {
  data: Vendor[];
  hierarchy?: VendorHierarchy;
}

export interface SupplierOption {
  label: string;
  value: string;
  isParent?: boolean;
  isChild?: boolean;
}

export interface SupplierFetchResult {
  options: SupplierOption[];
  hierarchyMap: Record<string, string[]>;
}

const transformVendors = (
  vendors: Vendor[],
  hierarchy?: VendorHierarchy,
): SupplierFetchResult => {
  const optionsMap = new Map<string, SupplierOption>();
  const subVendorsMap: Record<string, string[]> = hierarchy?.subVendors || {};

  const parentIds = new Set(
    Object.keys(subVendorsMap).filter(
      (id) => (subVendorsMap[id] || []).length > 0,
    ),
  );

  const childIdsSet = new Set<string>();
  Object.values(subVendorsMap).forEach((children) => {
    children.forEach((childId) => childIdsSet.add(childId));
  });

  vendors.forEach((vendor) => {
    if (vendor.vendorId) {
      const isParent = parentIds.has(vendor.vendorId);
      const isChild = childIdsSet.has(vendor.vendorId);

      optionsMap.set(vendor.vendorId, {
        label: `${vendor.companyName} - ${vendor.vendorId}`,
        value: vendor.vendorId,
        isParent: isParent,
        isChild: isChild,
      });
    }
  });

  Object.entries(subVendorsMap).forEach(([parentId, childIds]) => {
    const parentVendor = optionsMap.get(parentId);
    const parentName = parentVendor
      ? parentVendor.label.split(" - ")[0]
      : "Sub-Vendor";

    childIds.forEach((childId) => {
      if (!optionsMap.has(childId)) {
        optionsMap.set(childId, {
          label: `${parentName} - ${childId}`,
          value: childId,
          isParent: false,
          isChild: true,
        });
      }
    });
  });

  return {
    options: Array.from(optionsMap.values()),
    hierarchyMap: subVendorsMap,
  };
};

export const fetchOnBoardVendors = async (): Promise<SupplierFetchResult> => {
  try {
    const response = await axios.get<VendorResponse>(
      "/api/vendor/onBoard/Search",
    );

    return transformVendors(
      response.data?.data || [],
      response.data?.hierarchy,
    );
  } catch (error) {
    console.error("Failed to fetch onboard vendors:", error);
    return { options: [], hierarchyMap: {} };
  }
};

export const fetchOnBoardVendorDetails = async (
  vendorId?: string,
): Promise<SupplierFetchResult> => {
  try {
    const currentVendorId = vendorId || localStorage.getItem("vendorId") || "";

    const response = await axios.get<VendorResponse>(
      "/api/vendor/onBoard/vendorDetails",
      {
        params: currentVendorId ? { vendorId: currentVendorId } : {},
      },
    );

    return transformVendors(
      response.data?.data || [],
      response.data?.hierarchy,
    );
  } catch (error) {
    console.error("Failed to fetch onboard vendor hierarchy details:", error);
    return { options: [], hierarchyMap: {} };
  }
};
